// Token persistence.
//
// SECURITY NOTE — chosen tradeoff:
//   The brief asked for httpOnly cookie OR sessionStorage, with httpOnly
//   preferred. httpOnly requires the *server* to set Set-Cookie; the client
//   cannot create an httpOnly cookie. Today core/'s POST /auth/verify returns
//   the JWT in the JSON body — there's no Set-Cookie path. Adding one is
//   backend work, which the brief explicitly puts out of scope ("don't touch
//   core/").
//
//   So: we use sessionStorage. This is XSS-vulnerable (any injected script
//   can read the token) but immune to CSRF, and the token clears on tab
//   close. Once core/ ships an httpOnly cookie flow, swap this module's
//   internals to a no-op (the cookie travels via `credentials: 'include'`)
//   and remove the Authorization header injection in api.js.
//
//   We deliberately avoid localStorage — sessionStorage's per-tab lifetime
//   is closer to the security profile customers expect from a SaaS console.

const TOKEN_KEY = 'alfred.token';
const USER_KEY = 'alfred.user';
const TENANT_KEY = 'alfred.tenant';
// Platform-owner only: the tenant the owner switched INTO via the tenant
// switcher. When set, api.js sends it as the X-Tenant-Id header and the whole
// dashboard re-scopes to it. Distinct from TENANT_KEY (the owner's own login
// tenant), so clearing the switch reverts cleanly.
const SELECTED_TENANT_KEY = 'alfred.selectedTenant';
// Platform-owner only: the tenant the owner wants to LAND ON each session (their
// "home"/default project). Unlike everything else in this module it lives in
// localStorage ON PURPOSE — sessionStorage clears on tab close, which is why the
// switcher kept reverting to the login tenant every session. This is a durable
// preference, not a credential, so it survives across sessions and logout.

export function getStoredToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage disabled — silently ignore */
  }
}

export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredUser(user) {
  try {
    if (user) sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredTenant() {
  try {
    const raw = sessionStorage.getItem(TENANT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredTenant(tenant) {
  try {
    if (tenant) sessionStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
    else sessionStorage.removeItem(TENANT_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredSelectedTenant() {
  try {
    const raw = sessionStorage.getItem(SELECTED_TENANT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredSelectedTenant(tenant) {
  try {
    if (tenant) sessionStorage.setItem(SELECTED_TENANT_KEY, JSON.stringify(tenant));
    else sessionStorage.removeItem(SELECTED_TENANT_KEY);
  } catch {
    /* ignore */
  }
}

// localStorage, keyed separately so it outlives the session and a logout. NB the
// key string still lives under the same 'alfred.' namespace as the rest.
const DEFAULT_TENANT_KEY = 'alfred.defaultTenant';

// Stored as { ownerEmail, tenant }. localStorage is browser-scoped, not
// user-scoped, so we stamp the owner's email and only honor the default for that
// same owner — otherwise a second owner logging in on the same browser would
// inherit the first one's default. Pass the current owner's email to read it back.
export function getStoredDefaultTenant(ownerEmail) {
  try {
    const raw = localStorage.getItem(DEFAULT_TENANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.tenant) return null;
    if (ownerEmail && parsed.ownerEmail && parsed.ownerEmail !== ownerEmail) return null;
    return parsed.tenant;
  } catch {
    return null;
  }
}

export function setStoredDefaultTenant(tenant, ownerEmail) {
  try {
    if (tenant) {
      localStorage.setItem(DEFAULT_TENANT_KEY, JSON.stringify({ ownerEmail: ownerEmail || null, tenant }));
    } else {
      localStorage.removeItem(DEFAULT_TENANT_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearStoredAuth() {
  setStoredToken(null);
  setStoredUser(null);
  setStoredTenant(null);
  setStoredSelectedTenant(null);
  // Intentionally NOT clearing the default tenant — it's a durable preference,
  // not a credential, and the owner wants to land on it again next login.
}
